import styled from 'styled-components';
import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiLock, mdiAlertCircle, mdiGoogle } from '@mdi/js';
import { useAuth } from '../../../context/AuthContext';
import { GlassCard } from '../../ui/GlassCard';
import { Button } from '../../ui/Button';
import { Container } from '../../ui/Container';
import { PageHead } from '../../seo';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-8);
`;

const LoginCard = styled(GlassCard)`
  width: 100%;
  max-width: 400px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--space-8);
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4);
  color: white;
`;

const Title = styled.h1`
  font-size: var(--text-2xl);
  margin-bottom: var(--space-2);
`;

const Subtitle = styled.p`
  color: var(--color-text-tertiary);
  font-size: var(--text-sm);
`;

const Form = styled.form``;

const FormGroup = styled.div`
  margin-bottom: var(--space-4);
`;

const Label = styled.label`
  display: block;
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--text-base);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px rgba(81, 20, 97, 0.1);
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
  background: rgba(255, 59, 48, 0.1);
  color: var(--color-error);
  font-size: var(--text-sm);
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: var(--space-6) 0;
  color: var(--color-text-tertiary);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border);
  }
`;

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      navigate('/admin');
    } catch (err) {
      const message =
        err instanceof Error && err.message.includes('not authorized')
          ? err.message
          : 'Google sign-in failed. Please try again.';
      setError(message);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <PageWrapper>
      <PageHead title="Admin Login" description="Sign in to the admin dashboard." noindex />
      <Container>
        <LoginCard>
          <Header>
            <IconWrapper>
              <Icon path={mdiLock} size={1.5} />
            </IconWrapper>
            <Title>Admin Login</Title>
            <Subtitle>Sign in to access the dashboard</Subtitle>
          </Header>

          {error && (
            <ErrorMessage>
              <Icon path={mdiAlertCircle} size={0.8} />
              {error}
            </ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </FormGroup>

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Form>

          <Divider>or</Divider>

          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={handleGoogle}
            disabled={googleLoading}
            icon={<Icon path={mdiGoogle} size={0.8} />}
          >
            {googleLoading ? 'Signing in...' : 'Sign in with Google'}
          </Button>
        </LoginCard>
      </Container>
    </PageWrapper>
  );
}
